/*
 * <twc-whisky-spotlight> — the homepage spotlight band's moving parts.
 *
 * It owns exactly three things, and nothing that CSS or Liquid already does:
 *   1. the outer tab group  — "Whisky of the month" / "Monthly add-ins"
 *   2. the tasting switcher — Colour / Nose / Palate
 *   3. the add-ins carousel — arrows at every width, dots on a phone
 *
 * PROGRESSIVE BY CONSTRUCTION. sections/twc-whisky-spotlight.liquid paints the
 * opening panel and the opening tasting tab server-side, and the read-more
 * toggle is a CSS checkbox, so a visitor whose JavaScript never lands still
 * reads a complete band and can still swipe the add-ins. The arrows and the
 * dots are the one thing that cannot work without this file, so the section
 * keeps them hidden until `data-twc-ready` is stamped here.
 *
 * The two tab groups share one controller because they behave identically —
 * roving tabindex, automatic activation, Home/End/Arrow keys (WAI-ARIA tabs).
 * They differ only in which elements they own, so `wire()` is handed a scope.
 *
 * Every listener rides ONE AbortController, per the theme's custom-element
 * rule: connectedCallback opens it, disconnectedCallback aborts the lot.
 */
(function (global) {
  'use strict';

  if (global.customElements && global.customElements.get('twc-whisky-spotlight')) return;

  var TAB = '[data-twc-spotlight-tab]';
  var PANEL = '[data-twc-spotlight-panel]';
  var NOTE_GROUP = '[data-twc-spotlight-notes]';
  var NOTE_TAB = '[data-twc-spotlight-note-tab]';
  var NOTE_PANEL = '[data-twc-spotlight-note-panel]';

  function list(root, selector) {
    return Array.prototype.slice.call(root.querySelectorAll(selector));
  }

  function reducedMotion() {
    return (
      global.matchMedia && global.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  var TwcWhiskySpotlight = class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      var signal = this.controller.signal;

      /* ---- 1. the outer tabs -------------------------------------------- */
      var tabs = list(this, TAB);
      var panels = list(this, PANEL);
      this.wire(tabs, panels, signal);

      /* ---- 2. one tasting switcher per panel ---------------------------- */
      // Scoped to its own group: a future panel with its own notes must not
      // steal the selection of another.
      var self = this;
      list(this, NOTE_GROUP).forEach(function (group) {
        self.wire(list(group, NOTE_TAB), list(group, NOTE_PANEL), signal);
      });

      /* ---- 3. the carousel ---------------------------------------------- */
      this.track = this.querySelector('[data-twc-spotlight-track]');
      this.prev = this.querySelector('[data-twc-spotlight-prev]');
      this.next = this.querySelector('[data-twc-spotlight-next]');
      this.dots = list(this, '[data-twc-spotlight-dot]');
      this.current = 0;
      if (this.track) this.wireCarousel(signal);

      this.setAttribute('data-twc-ready', '');
    }

    disconnectedCallback() {
      if (this.controller) this.controller.abort();
      this.track = null;
      this.prev = null;
      this.next = null;
      this.dots = [];
    }

    /* ---- tab groups ----------------------------------------------------- */

    wire(tabs, panels, signal) {
      if (tabs.length < 2) return;
      var self = this;

      tabs.forEach(function (tab, index) {
        tab.addEventListener(
          'click',
          function () {
            self.select(tabs, panels, index, false);
          },
          { signal: signal }
        );

        tab.addEventListener(
          'keydown',
          function (event) {
            var target = -1;
            if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
              target = (index + 1) % tabs.length;
            } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
              target = (index - 1 + tabs.length) % tabs.length;
            } else if (event.key === 'Home') {
              target = 0;
            } else if (event.key === 'End') {
              target = tabs.length - 1;
            }
            if (target < 0) return;
            event.preventDefault();
            self.select(tabs, panels, target, true);
          },
          { signal: signal }
        );
      });
    }

    select(tabs, panels, index, focus) {
      tabs.forEach(function (tab, i) {
        var on = i === index;
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.setAttribute('tabindex', on ? '0' : '-1');
      });

      // Tabs and panels are emitted in the same order and hidden the same way,
      // so the index is the whole mapping — no id bookkeeping to drift.
      panels.forEach(function (panel, i) {
        if (i === index) {
          panel.removeAttribute('hidden');
        } else {
          panel.setAttribute('hidden', '');
        }
      });

      if (focus && tabs[index]) tabs[index].focus();

      // A carousel measured while its panel was hidden has a zero width, so
      // the slide is re-seated the moment the panel can be seen.
      if (this.track) this.refresh(false);
    }

    /* ---- carousel ------------------------------------------------------- */

    wireCarousel(signal) {
      var self = this;

      if (this.prev) {
        this.prev.addEventListener(
          'click',
          function () {
            self.step(-1);
          },
          { signal: signal }
        );
      }

      if (this.next) {
        this.next.addEventListener(
          'click',
          function () {
            self.step(1);
          },
          { signal: signal }
        );
      }

      this.dots.forEach(function (dot, index) {
        dot.addEventListener(
          'click',
          function () {
            self.goTo(index);
          },
          { signal: signal }
        );
      });

      // One frame per scroll burst: the handler only reads geometry, and the
      // browser fires scroll far faster than the dots need repainting.
      this.pending = false;
      this.track.addEventListener(
        'scroll',
        function () {
          if (self.pending) return;
          self.pending = true;
          global.requestAnimationFrame(function () {
            self.pending = false;
            self.refresh(true);
          });
        },
        { passive: true, signal: signal }
      );

      global.addEventListener(
        'resize',
        function () {
          self.refresh(false);
        },
        { signal: signal }
      );

      this.refresh(true);
    }

    last() {
      return (this.dots.length || 1) - 1;
    }

    step(direction) {
      this.goTo(this.current + direction);
    }

    goTo(index) {
      this.current = Math.max(0, Math.min(this.last(), index));
      this.track.scrollTo({
        left: this.current * this.track.clientWidth,
        behavior: reducedMotion() ? 'auto' : 'smooth',
      });
    }

    /*
     * `fromScroll` says who moved. A scroll is the visitor choosing a slide, so
     * the position teaches the index. Anything else — a resize, or the panel
     * being shown after being measured at zero width — changed the geometry
     * UNDER the visitor, so the index is the truth and the position is re-seated
     * from it. Reading the position at that moment would land on whichever
     * slide the old arithmetic happens to point at.
     */
    refresh(fromScroll) {
      if (!this.track) return;
      var width = this.track.clientWidth;

      if (!width) return;
      if (fromScroll) {
        this.current = Math.max(0, Math.min(this.last(), Math.round(this.track.scrollLeft / width)));
      } else {
        this.track.scrollLeft = this.current * width;
      }

      var index = this.current;
      this.dots.forEach(function (dot, i) {
        dot.setAttribute('aria-current', i === index ? 'true' : 'false');
      });

      // The track does not wrap, so the ends say so rather than pretending.
      if (this.prev) this.prev.disabled = index <= 0;
      if (this.next) this.next.disabled = index >= this.last();
    }
  };

  if (global.customElements) {
    global.customElements.define('twc-whisky-spotlight', TwcWhiskySpotlight);
  }
})(window);
